import React from 'react';

import SingleSelect, {SingleSelectItem} from '@components/single-select';

import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';

import range from 'lodash/range';
import isNil from 'lodash/isNil';
import './TimeField.styl';
import clamp from 'lodash/clamp';
import partition from 'lodash/partition';
import Time from 'records/Time';

const minuteOptions = [
  {label: '00', value: 0},
  {label: '15', value: 15},
  {label: '30', value: 30},
  {label: '45', value: 45},
];

enum AM_PM {
  AM = 'am',
  PM = 'pm',
}

/**
 * Returns two ranges of options for am and pm
 *
 * @param start
 * @param end
 * @returns {*[]}
 */
function rangeToOptions([start, end]) {
  const [am, pm] = partition(range(start, end + 1), x => x < 12);

  const toOptions = range =>
    range.map(i => ({
      label: i === 0 ? '12' : i + '',
      value: i % 12,
    }));

  return [toOptions(am), toOptions(pm.map(x => x - 12))];
}

interface Props {
  value: Time;
  onChange: (time: Time) => void;
  renderMinutes?: boolean;
  timeRange?: [number, number];
}

interface State {
  period: AM_PM;
}

export default class TimeField extends React.PureComponent<Props, State> {
  static defaultProps = {
    // 0 being midnight, 12am
    timeRange: [0, 23],
    renderMinutes: true,
  };

  constructor(props) {
    super(props);
    const {hour} = this.props.value;
    this.state = {
      period: !hour || hour < 12 ? AM_PM.AM : AM_PM.PM,
    };
  }

  componentDidUpdate(prevProps) {
    const {value} = this.props;
    if (value !== prevProps.value && value.hour >= 12 && this.state.period !== AM_PM.PM) {
      this.setState({period: AM_PM.PM});
    }
    if (value !== prevProps.value && value.hour < 12 && this.state.period !== AM_PM.AM) {
      this.setState({period: AM_PM.AM});
    }
  }

  handleChangeAmPm = evt => {
    const {value, onChange, timeRange} = this.props;
    const period = evt.target.value;
    this.setState({period});
    const isPm = period === AM_PM.PM;
    const hour = clamp(value.hour + (isPm ? 12 : -12), ...timeRange!);
    onChange(value.set('hour', hour));
  };

  render() {
    const {value: time, onChange, renderMinutes, timeRange} = this.props;
    const {period} = this.state;
    const isPM = period === AM_PM.PM;
    const [amRange, pmRange] = rangeToOptions(timeRange!);

    return (
      <div className="ev-time-field">
        <SingleSelect
          value={isNil(time.hour) ? null : time.hour % 12}
          onChange={event => {
            let value = event.target.value;
            value = value % 12;
            onChange(time.set('hour', isPM ? value + 12 : value));
          }}>
          {(isPM ? pmRange : amRange).map(({label, value}) => (
            <SingleSelectItem key={value} value={value}>
              {label}
            </SingleSelectItem>
          ))}
        </SingleSelect>
        {renderMinutes && (
          <SingleSelect
            value={time.minute}
            onChange={evt => onChange(time.set('minute', evt.target.value))}>
            {minuteOptions.map(({label, value}) => (
              <SingleSelectItem key={value} value={value}>
                {label}
              </SingleSelectItem>
            ))}
          </SingleSelect>
        )}
        <RadioGroup style={{flexDirection: 'row'}} value={period} onChange={this.handleChangeAmPm}>
          <FormControlLabel value={AM_PM.AM} control={<Radio />} label={AM_PM.AM} />
          <FormControlLabel value={AM_PM.PM} control={<Radio />} label={AM_PM.PM} />
        </RadioGroup>
      </div>
    );
  }
}
